import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Loader2 } from "lucide-react";

import { browseApi } from "@/api/browse";
import { logger } from "@/utils/logger";

const fallbackImages = {
  equine: "https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?w=400&h=300&fit=crop",
  canine: "https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=400&h=300&fit=crop",
  feline: "https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=400&h=300&fit=crop",
  exotic: "https://images.unsplash.com/photo-1425082661705-1834bfd09dca?w=400&h=300&fit=crop",
  avian: "https://images.unsplash.com/photo-1452570053594-1b985d6ea890?w=400&h=300&fit=crop",
  others: "https://images.unsplash.com/photo-1516467508483-a7212febe31a?w=400&h=300&fit=crop",  // Farm animals
};

const CategoryCard = ({ cat }) => {
  const [imgError, setImgError] = useState(false);
  const imgSrc = imgError ? (fallbackImages[cat.species] || fallbackImages.exotic) : cat.image;
  
  return (
    <Link
      to={`/browse/${cat.species}`}
      className="group relative aspect-[4/3] rounded-2xl overflow-hidden block bg-[#18181B]"
      data-testid={`category-${cat.species}`}
    >
      <img
        src={imgSrc}
        alt={cat.name}
        className="absolute top-0 left-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        loading="lazy"
        decoding="async"
        crossOrigin="anonymous"
        referrerPolicy="no-referrer"
        onError={() => setImgError(true)}
      />
      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
      <div className="absolute top-0 left-0 w-full h-full flex flex-col justify-end p-4">
        <h3 className="text-lg font-medium text-[#EDEDED] mb-1">{cat.name}</h3>
        <p className="text-sm text-[#A1A1AA]">{cat.count} listings</p>
      </div>
      <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <ChevronRight className="w-5 h-5 text-[#D4AF37]" />
      </div>
    </Link>
  );
};

const CategoryCarousel = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const run = async () => {
      try {
        setError(null);
        const data = await browseApi.categories();
        setCategories(data.categories || []);
      } catch (err) {
        logger.error("Failed to fetch categories:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-[#A1A1AA]">Unable to load categories. Please refresh the page.</p>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-[#A1A1AA]">No categories available.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6">
      {categories.map((cat) => (
        <CategoryCard key={cat.species} cat={cat} />
      ))}
    </div>
  );
};

export default CategoryCarousel;
